import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CaretLeft, CaretRight, CircleNotch, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { SegmentedFilter } from "@/components/ui/segmented-filter";
import { apiGet, apiSend } from "@/lib/api-error";
import { todayYmd } from "@/lib/company-expiry";
import { formatDateShort } from "@/lib/format";
import { toast } from "@/lib/toast";
import type { FiscalDay, FiscalRow } from "@/types";
import { SOURCE_FILTER_OPTIONS, shiftYmd, type SourceFilter } from "./fiscal-labels";
import { FiscalDayTable } from "./FiscalDayTable";

type Items = { source: FiscalRow["source"]; sourceId: string }[];

export function FiscalizationPage() {
  const queryClient = useQueryClient();
  const [date, setDate] = useState(() => todayYmd());
  const [filter, setFilter] = useState<SourceFilter>("all");
  const [resending, setResending] = useState<FiscalRow | null>(null);

  const { data: day, isLoading } = useQuery({
    queryKey: ["admin", "fiscalization", date],
    queryFn: () => apiGet<FiscalDay>(`/api/admin/fiscalization?date=${date}`, "Greška pri učitavanju liste"),
  });

  const toggle = useMutation({
    mutationFn: ({ items, fiscalized }: { items: Items; fiscalized: boolean }) =>
      apiSend("/api/admin/fiscalization/marks", "POST", { items, fiscalized }, "Greška pri označavanju"),
    onError: (e: Error) => toast.error(e.message),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["admin", "fiscalization"] }),
  });

  const resend = useMutation({
    mutationFn: (row: FiscalRow) =>
      apiSend<{ result: string }>(
        `/api/admin/fiscalization/confirmation/${row.source}/${encodeURIComponent(row.sourceId)}/resend`,
        "POST",
        undefined,
        "Greška pri slanju potvrde",
      ),
    onSuccess: ({ result }) => {
      if (result === "not_applicable") toast.error("Potvrda nije dostupna za ovu uplatu.");
      else toast.success("Potvrda je ponovo poslana.");
    },
    onError: (e: Error) => toast.error(e.message),
    onSettled: () => setResending(null),
  });

  const rows = (day?.rows ?? []).filter((r) => filter === "all" || r.source === filter);

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-foreground">Fiskalizacija</h1>
        <p className="text-sm text-muted-foreground">Uplate i dokumenti za fiskalnu kasu, po danu.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <Button size="icon" variant="outline" aria-label="Prethodni dan" onClick={() => setDate(shiftYmd(date, -1))}>
            <CaretLeft className="size-4" />
          </Button>
          <Input
            type="date"
            value={date}
            onChange={(e) => e.target.value && setDate(e.target.value)}
            className="w-40"
            aria-label="Dan"
          />
          <Button size="icon" variant="outline" aria-label="Sljedeći dan" onClick={() => setDate(shiftYmd(date, 1))}>
            <CaretRight className="size-4" />
          </Button>
        </div>
        <span className="text-sm text-muted-foreground">{formatDateShort(`${date}T12:00:00Z`)}</span>
        <SegmentedFilter options={SOURCE_FILTER_OPTIONS} value={filter} onChange={setFilter} ariaLabel="Vrsta" />
        {isLoading && <CircleNotch className="size-4 animate-spin text-muted-foreground" />}
      </div>

      {day && day.backlog.count > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm">
          <span className="flex items-center gap-2 text-foreground">
            <Warning className="size-4 text-amber-600" weight="fill" />
            {day.backlog.count} uplata od ranije nije fiskalizovano.
          </span>
          {day.backlog.oldestDate && (
            <Button size="sm" variant="outline" onClick={() => setDate(day.backlog.oldestDate!)}>
              Prikaži najstariji dan
            </Button>
          )}
        </div>
      )}

      {day && (
        <FiscalDayTable
          day={day}
          rows={rows}
          onToggle={(items, fiscalized) => toggle.mutate({ items, fiscalized })}
          onResend={setResending}
        />
      )}

      <ConfirmDialog
        open={resending !== null}
        title="Poslati potvrdu o uplati ponovo klijentu?"
        confirmLabel="Pošalji ponovo"
        isPending={resend.isPending}
        onClose={() => setResending(null)}
        onConfirm={() => resending && resend.mutate(resending)}
      />
    </div>
  );
}
